import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, Truck, Shield, Package, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const TruckingInsurance = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Commercial Auto / Trucking / Fleets',
      subtitle: 'Insurance Solutions for Your Commercial Vehicles',
      intro: 'Specialized coverage for work vehicles, owner-operators, hot shot carriers, and fleets of all sizes.',
      
      whoWeServe: 'Who We Serve',
      clientTypes: [
        {
          icon: Truck,
          title: 'Owner-Operators',
          description: 'Independent truckers operating their own commercial vehicles'
        },
        {
          icon: Package,
          title: 'Hot Shot Carriers',
          description: 'Time-sensitive freight delivery with smaller commercial trucks'
        },
        {
          icon: Shield,
          title: 'Fleet Operations',
          description: 'Businesses managing multiple commercial vehicles'
        }
      ],

      operationTypesTitle: 'Types of Operations We Quote',
      operationTypesIntro: 'We work with a variety of commercial trucking operations. Tell us about your business to receive an accurate quote:',
      operationTypes: [
        'General Freight & Cargo',
        'Refrigerated Transport',
        'Auto Transport / Vehicle Hauling',
        'Tow Trucks & Recovery Services',
        'Courier & Hot Shot Delivery',
        'Sand, Gravel & Aggregate Hauling',
        'Agricultural Products & Livestock',
        'Contractor Vehicles & Equipment'
      ],
      
      quoteQuestionsTitle: 'What We Need to Know',
      quoteQuestionsIntro: 'To provide an accurate commercial auto insurance quote, we need to understand your operation:',
      quoteQuestions: [
        {
          q: 'What do you haul?',
          detail: 'Type of cargo, materials, or services you provide with your commercial vehicles'
        },
        {
          q: 'Is it your own cargo or hauling for others?',
          detail: 'Whether you transport your own goods or provide for-hire transportation services'
        },
        {
          q: 'What equipment do you operate?',
          detail: 'Types and number of trucks, trailers, or specialized commercial vehicles'
        },
        {
          q: 'What is your operating radius?',
          detail: 'Local, regional, or long-haul; intrastate or interstate operations'
        },
        {
          q: 'Do you have a USDOT number?',
          detail: 'Required for certain interstate and for-hire operations'
        }
      ],

      coverageTitle: 'Coverage Options',
      coverageIntro: 'Commercial trucking insurance typically includes the following options, subject to your specific policy and insurance carrier. Coverage requirements and availability may vary:',
      coverageTypes: [
        {
          name: 'Commercial Auto Liability',
          description: 'Protects against bodily injury and property damage claims when your commercial vehicle causes harm to others. Required for most commercial operations.'
        },
        {
          name: 'Physical Damage Coverage',
          description: 'Covers damage to your own commercial vehicles from collision, comprehensive events, or other covered perils.'
        },
        {
          name: 'Motor Truck Cargo Insurance',
          description: 'Covers the cargo you are hauling against loss or damage during transit. May be required by shippers or contracts.'
        },
        {
          name: 'General Liability',
          description: 'Protects your business from claims not directly related to vehicle operation, such as premises liability or business operations.'
        }
      ],

      faqTitle: 'Frequently Asked Questions',
      faqItems: [
        {
          q: 'What is Non-Trucking Liability (NTL)?',
          a: 'Non-Trucking Liability coverage is designed for owner-operators under permanent lease to a motor carrier. It provides liability coverage when the listed commercial vehicle is used for the operator\'s personal, non-commercial purposes. The motor carrier typically provides primary commercial auto liability while the vehicle is dispatched. NTL fills the gap during personal use. Coverage terms and eligibility depend on your specific policy, lease agreement, and insurance carrier guidelines.'
        },
        {
          q: 'Are NTL and Bobtail the same thing?',
          a: 'While these terms are sometimes used interchangeably in casual conversation, they can have different meanings depending on the insurance carrier and policy. Some carriers use "Bobtail" to describe physical operation (driving a truck-tractor without a trailer attached), while "Non-Trucking Liability" focuses on the purpose of the trip (personal vs. commercial use). Driving without a trailer or with an empty trailer does not automatically mean the trip is personal—it depends on whether you are operating under dispatch or for the motor carrier\'s benefit. Always review your specific policy definitions and speak with your agent to understand exactly what your coverage includes.'
        },
        {
          q: 'What do Deadhead and Unladen mean?',
          a: 'These terms describe operating conditions but do not automatically define coverage eligibility. "Deadhead" typically means operating a commercial vehicle without cargo, often while still under dispatch or between loads for commercial purposes. "Unladen" means operating without a load or with an empty trailer. Neither deadhead nor unladen operation automatically qualifies as personal use covered under Non-Trucking Liability—coverage depends on whether you are operating under the motor carrier\'s authority or for personal reasons. Policy definitions and exclusions vary by carrier.'
        },
        {
          q: 'Why should I review my lease agreement before selecting coverage?',
          a: 'Your lease agreement with the motor carrier defines your relationship, responsibilities, and coverage arrangements. It typically specifies who provides primary liability coverage during dispatched operations and under what circumstances. Understanding your lease terms helps you identify coverage gaps and determine whether you need Non-Trucking Liability, Contingent Liability, or other coverages. Some lease agreements require specific insurance types or limits. Your agent can review your lease alongside your insurance policies to ensure proper protection and compliance.'
        }
      ],

      faqNote: 'Coverage definitions, exclusions, and eligibility requirements vary significantly by insurance carrier, policy form, and state regulations. The information above is general in nature. Contact our agency to review your specific lease agreement, operation type, and insurance policies to determine appropriate coverage for your situation.',

      disclaimer: 'Important Notice',
      disclaimerText: 'Coverage options listed are subject to policy terms, underwriting requirements, and insurance carrier guidelines. Not all coverages are automatic or required for every operation. Specific limits, deductibles, and rates depend on your business type, vehicles, driving history, and other factors.',

      contactTitle: 'Get a Custom Quote',
      contactIntro: 'Commercial trucking insurance requires detailed evaluation. Contact us to discuss your specific needs and obtain an accurate quote.',
      
      truckingSpecialist: 'Trucking Insurance Specialist',
      pierreInfo: 'Pierre Whiteside specializes in trucking and commercial auto insurance. Reach out directly for expert guidance.',
      
      officePhone: 'Office Phone',
      directContact: 'Direct Contact - Pierre Whiteside',
      
      quoteRequest: 'Request a Quote',
      quoteIntro: 'To provide an accurate quote, we need some information about your operation:',
      quoteInfo: [
        'Business/Company Name',
        'Type of Operation (owner-operator, fleet, hot shot, etc.)',
        'USDOT Number (if applicable)',
        'Number of Units/Vehicles',
        'Types of Cargo Hauled',
        'Operating Radius'
      ],
      
      emailSubject: 'Commercial Auto/Trucking Insurance Quote Request',
      callButton: 'Call Office',
      emailPierreButton: 'Email Pierre Whiteside',
      
      noteTitle: 'How to Request a Quote',
      noteText: 'Click the button below to send an email with your business details, or call us directly. Include your company name, operation type, USDOT number (if applicable), and number of vehicles. We do not request sensitive financial information via email or chat.'
    },
    es: {
      title: 'Auto Comercial / Camiones / Flotillas',
      subtitle: 'Soluciones de Seguros para sus Vehículos Comerciales',
      intro: 'Cobertura especializada para vehículos de trabajo, operadores propietarios, transportistas hot shot y flotillas de todos los tamaños.',
      
      whoWeServe: 'A Quién Servimos',
      clientTypes: [
        {
          icon: Truck,
          title: 'Operadores Propietarios',
          description: 'Camioneros independientes que operan sus propios vehículos comerciales'
        },
        {
          icon: Package,
          title: 'Transportistas Hot Shot',
          description: 'Entrega de carga urgente con camiones comerciales más pequeños'
        },
        {
          icon: Shield,
          title: 'Operaciones de Flotilla',
          description: 'Empresas que administran múltiples vehículos comerciales'
        }
      ],

      coverageTitle: 'Opciones de Cobertura',
      coverageIntro: 'El seguro de camiones comerciales típicamente incluye las siguientes opciones, sujetas a su póliza específica y aseguradora. Los requisitos y disponibilidad de cobertura pueden variar:',
      coverageTypes: [
        {
          name: 'Responsabilidad Civil de Auto Comercial',
          description: 'Protege contra reclamos por lesiones corporales y daños a la propiedad cuando su vehículo comercial causa daño a terceros. Requerida para la mayoría de operaciones comerciales.'
        },
        {
          name: 'Cobertura de Daño Físico',
          description: 'Cubre daños a sus propios vehículos comerciales por colisión, eventos comprensivos u otros peligros cubiertos.'
        },
        {
          name: 'Seguro de Carga de Camión',
          description: 'Cubre la carga que transporta contra pérdida o daño durante el tránsito. Puede ser requerido por embarcadores o contratos.'
        },
        {
          name: 'Responsabilidad General',
          description: 'Protege su negocio de reclamos no relacionados directamente con la operación del vehículo, como responsabilidad de las instalaciones u operaciones comerciales.'
        }
      ],

      faqTitle: 'Preguntas Frecuentes',
      faqItems: [
        {
          q: '¿Qué es Non-Trucking Liability (NTL)?',
          a: 'La cobertura Non-Trucking Liability está diseñada para operadores propietarios bajo arrendamiento permanente a un transportista. Proporciona cobertura de responsabilidad civil cuando el vehículo comercial listado se usa para propósitos personales no comerciales del operador. El transportista típicamente proporciona responsabilidad civil comercial primaria mientras el vehículo está despachado. NTL llena el vacío durante uso personal. Los términos de cobertura y elegibilidad dependen de su póliza específica, contrato de arrendamiento y directrices de la aseguradora.'
        },
        {
          q: '¿NTL y Bobtail son lo mismo?',
          a: 'Aunque estos términos a veces se usan indistintamente en conversación casual, pueden tener significados diferentes dependiendo de la aseguradora y póliza. Algunas aseguradoras usan "Bobtail" para describir operación física (conducir un camión-tractor sin remolque), mientras que "Non-Trucking Liability" se enfoca en el propósito del viaje (uso personal vs. comercial). Conducir sin remolque o con remolque vacío no significa automáticamente que el viaje sea personal—depende de si está operando bajo despacho o para beneficio del transportista. Siempre revise las definiciones específicas de su póliza y hable con su agente para entender exactamente qué incluye su cobertura.'
        },
        {
          q: '¿Qué significan Deadhead y Unladen?',
          a: 'Estos términos describen condiciones de operación pero no definen automáticamente elegibilidad de cobertura. "Deadhead" típicamente significa operar un vehículo comercial sin carga, a menudo aún bajo despacho o entre cargas para propósitos comerciales. "Unladen" significa operar sin carga o con remolque vacío. Ni operación deadhead ni unladen califican automáticamente como uso personal cubierto bajo Non-Trucking Liability—la cobertura depende de si está operando bajo autoridad del transportista o por razones personales. Las definiciones y exclusiones de póliza varían por aseguradora.'
        },
        {
          q: '¿Por qué debo revisar mi contrato de arrendamiento antes de seleccionar cobertura?',
          a: 'Su contrato de arrendamiento con el transportista define su relación, responsabilidades y arreglos de cobertura. Típicamente especifica quién proporciona cobertura de responsabilidad primaria durante operaciones despachadas y bajo qué circunstancias. Entender los términos de su arrendamiento le ayuda a identificar brechas de cobertura y determinar si necesita Non-Trucking Liability, Responsabilidad Contingente u otras coberturas. Algunos contratos de arrendamiento requieren tipos específicos de seguro o límites. Su agente puede revisar su arrendamiento junto con sus pólizas de seguro para asegurar protección apropiada y cumplimiento.'
        }
      ],

      faqNote: 'Las definiciones de cobertura, exclusiones y requisitos de elegibilidad varían significativamente por aseguradora, formulario de póliza y regulaciones estatales. La información anterior es de naturaleza general. Contacte nuestra agencia para revisar su contrato de arrendamiento específico, tipo de operación y pólizas de seguro para determinar cobertura apropiada para su situación.',

      disclaimer: 'Aviso Importante',
      disclaimerText: 'Las opciones de cobertura listadas están sujetas a términos de póliza, requisitos de suscripción y directrices de la aseguradora. No todas las coberturas son automáticas o requeridas para cada operación. Los límites específicos, deducibles y tarifas dependen del tipo de negocio, vehículos, historial de manejo y otros factores.',

      contactTitle: 'Obtenga una Cotización Personalizada',
      contactIntro: 'El seguro de camiones comerciales requiere evaluación detallada. Contáctenos para discutir sus necesidades específicas y obtener una cotización precisa.',
      
      truckingSpecialist: 'Especialista en Seguros de Trucking',
      pierreInfo: 'Pierre Whiteside se especializa en seguros de trucking y auto comercial. Contáctelo directamente para orientación experta.',
      
      officePhone: 'Teléfono de Oficina',
      directContact: 'Contacto Directo - Pierre Whiteside',
      
      quoteRequest: 'Solicitar Cotización',
      quoteIntro: 'Para proporcionar una cotización precisa, necesitamos información sobre su operación:',
      quoteInfo: [
        'Nombre del Negocio/Compañía',
        'Tipo de Operación (operador propietario, flotilla, hot shot, etc.)',
        'Número USDOT (si aplica)',
        'Número de Unidades/Vehículos',
        'Tipos de Carga Transportada',
        'Radio de Operación'
      ],
      
      emailSubject: 'Solicitud de Cotización Seguro Auto Comercial/Trucking',
      callButton: 'Llamar a Oficina',
      emailPierreButton: 'Enviar Email a Pierre Whiteside',
      
      noteTitle: 'Cómo Solicitar una Cotización',
      noteText: 'Haga clic en el botón a continuación para enviar un correo con los detalles de su negocio, o llámenos directamente. Incluya nombre de compañía, tipo de operación, número USDOT (si aplica) y número de vehículos. No solicitamos información financiera sensible por correo o chat.'
    }
  };

  const text = content[language];
  const emailSubject = encodeURIComponent(text.emailSubject);
  const emailBody = encodeURIComponent(
    `${text.quoteInfo.join('\n')}\n\n${language === 'en' ? 'Please provide:' : 'Por favor proporcione:'}\n- \n- \n- \n- `
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Truck className="w-12 h-12 md:w-16 md:h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {text.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-4">
              {text.subtitle}
            </p>
            <p className="text-lg text-blue-50">
              {text.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              {text.whoWeServe}
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {text.clientTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <Card key={index} className="text-center hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon className="w-8 h-8 text-blue-700" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {type.title}
                      </h3>
                      <p className="text-gray-600">
                        {type.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              {text.coverageTitle}
            </h2>
            <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
              {text.coverageIntro}
            </p>

            <div className="space-y-6">
              {text.coverageTypes.map((coverage, index) => (
                <Card key={index} className="border-l-4 border-blue-600">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {coverage.name}
                    </h3>
                    <p className="text-gray-700">
                      {coverage.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-amber-900 mb-2">{text.disclaimer}</h4>
                  <p className="text-amber-800 text-sm">
                    {text.disclaimerText}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Quote Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
              {text.contactTitle}
            </h2>
            <p className="text-lg text-gray-700 text-center mb-12">
              {text.contactIntro}
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Office Contact */}
              <Card className="border-2 border-blue-200">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {text.officePhone}
                  </h3>
                  <a
                    href="tel:+14694348287"
                    className="flex items-center gap-3 text-blue-700 hover:text-blue-800 text-lg font-semibold mb-6"
                  >
                    <Phone className="w-5 h-5" />
                    +1 (469) 434-8287
                  </a>
                  <a
                    href="tel:+14694348287"
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300"
                  >
                    {text.callButton}
                  </a>
                </CardContent>
              </Card>

              {/* Pierre Contact */}
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {text.truckingSpecialist}
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    {text.pierreInfo}
                  </p>
                  <div className="space-y-3 mb-6">
                    <a
                      href="tel:+12142800339"
                      className="flex items-center gap-2 text-green-700 hover:text-green-800 font-semibold"
                    >
                      <Phone className="w-4 h-4" />
                      +1 (214) 280-0339
                    </a>
                    <a
                      href="mailto:pwhiteside@palerminsurance.com"
                      className="flex items-center gap-2 text-green-700 hover:text-green-800 font-semibold text-sm break-all"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      pwhiteside@palerminsurance.com
                    </a>
                  </div>
                  <a
                    href={`mailto:pwhiteside@palerminsurance.com?subject=${emailSubject}&body=${emailBody}`}
                    className="block w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300"
                  >
                    {text.emailPierreButton}
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Quote Information */}
            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {text.quoteRequest}
                </h3>
                <p className="text-gray-700 mb-4">
                  {text.quoteIntro}
                </p>
                <ul className="space-y-2 mb-6">
                  {text.quoteInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <strong className="text-blue-900">{text.noteTitle}:</strong> {text.noteText}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TruckingInsurance;
